
import SlotMap from '@neumatter/slot-map'
import ObjectCache from './helpers/ObjectCache.js'
import Duration from './Duration.js'
import { balanceDuration } from './helpers/balance.js'
import { addISODate, differenceISODate } from './helpers/math.js'
import { isObject } from './helpers/is.js'
import { getDayOfWeek, getDayOfYear, getDaysInMonth, getOption, getWeekOfYear, isLeapYear } from './helpers/utils.js'
import { constrainToRange, regulateISODate, rejectToRange } from './helpers/regulate.js'
import PlainDate from './PlainDate.js'

const BUILTIN_CALENDAR_IDS = new Set([
  'iso8601',
  'hebrew',
  'islamic',
  'islamic-umalqura',
  'islamic-tbla',
  'islamic-civil',
  'islamic-rgsa',
  'islamicc',
  'persian',
  'ethiopic',
  'ethioaa',
  'coptic',
  'chinese',
  'dangi',
  'roc',
  'indian',
  'buddhist',
  'japanese',
  'gregory'
])

class ISO8601Calendar {
  constructor () {
    this.id = 'iso8601'
  }

  fields (fields) {
    return fields
  }

  /**
   *
   * @param {any} fieldsParam
   * @param {{}} options
   * @param {Calendar} calendar
   */
  dateFromFields (fieldsParam, options, calendar) {
    options = options === undefined ? {} : Object.create(options)
    const keys = calendar.fields(Object.keys(fieldsParam))
    let fields = keys.reduce((f, key) => {
      f[key] = fieldsParam[key]
      return f
    }, {})

    const overflow = getOption(
      options,
      'overflow',
      'string',
      ['constrain', 'reject'],
      'constrain'
    )

    fields = resolveNonLunisolarMonth(fields)

    const { year, month, day } = regulateISODate(
      fields.year,
      fields.month,
      fields.day,
      overflow
    )

    return new PlainDate(year, month, day, calendar)
  }

  dateAdd (dateParam, durationParam, overflow, calendar) {
    const date = dateParam
    const duration = Duration.from(durationParam)
    const { days } = balanceDuration(
      duration.days,
      duration.hours,
      duration.minutes,
      duration.seconds,
      duration.milliseconds,
      duration.microseconds,
      duration.nanoseconds,
      'day'
    )
    let year = date.year
    let month = date.month
    let day = date.day
    ;({ year, month, day } = addISODate(
      year,
      month,
      day,
      duration.years,
      duration.months,
      duration.weeks,
      days
    ))

    return new PlainDate(year, month, day, calendar)
  }

  dateUntil (oneParam, twoParam, optionsParam = { largestUnit: 'auto' }) {
    const one = oneParam
    const two = twoParam
    let largestUnit =
      isObject(optionsParam) && optionsParam.largestUnit
        ? optionsParam.largestUnit
        : 'auto'

    if (largestUnit === 'auto') {
      largestUnit = 'day'
    }

    const { years, months, weeks, days } = differenceISODate(
      one.year,
      one.month,
      one.day,
      two.year,
      two.month,
      two.day,
      largestUnit
    )

    return { years, months, weeks, days }
  }

  year (date) {
    return SlotMap.getInternal(date, 'year')
  }

  month (date) {
    return SlotMap.getInternal(date, 'month')
  }

  monthCode (date, leap = false) {
    return `M${date.month.toString().padStart(2, '0')}${leap ? 'L' : ''}`
  }

  day (date) {
    return SlotMap.getInternal(date, 'day')
  }

  era (date) {
    return undefined
  }

  eraYear (date) {
    return undefined
  }

  dayOfWeek (date) {
    return getDayOfWeek(date.year, date.month, date.day)
  }

  dayOfYear (date) {
    return getDayOfYear(date.year, date.month, date.day)
  }

  weekOfYear (date) {
    return getWeekOfYear(date.year, date.month, date.day)
  }

  daysInWeek () {
    return 7
  }

  daysInMonth (date) {
    return getDaysInMonth(date.year, date.month)
  }

  daysInYear (date) {
    if (typeof date.year === 'undefined') {
      return 365
    }

    return isLeapYear(date.year) ? 366 : 365
  }

  monthsInYear () {
    return 12
  }

  inLeapYear (date) {
    return isLeapYear(date.year || 1970)
  }
}

const calendars = new Map([['iso8601', new ISO8601Calendar()]])

export default class Calendar {
  static getPossibleIdentifiers () {
    return [...BUILTIN_CALENDAR_IDS]
  }

  static from (item) {
    if (typeof item === 'object' && item instanceof Calendar) {
      return item
    } else if (typeof item === 'string') {
      return new Calendar(item)
    } else {
      return new Calendar('iso8601')
    }
  }

  #id

  constructor (id) {
    // Note: if the argument is not passed, IsBuiltinCalendar("undefined") will fail. This check
    //       exists only to improve the error message.
    if (arguments.length < 1) {
      throw new RangeError('missing argument: id is required')
    }

    id = String(id)
    if (!BUILTIN_CALENDAR_IDS.has(id)) {
      throw new RangeError(`invalid calendar identifier ${id}`)
    }

    this.#id = id
  }

  get id () {
    return this.#id
  }

  fields (fields) {
    const fieldsArray = []
    const allowed = new Set([
      'year',
      'month',
      'monthCode',
      'day',
      'hour',
      'minute',
      'second',
      'millisecond',
      'microsecond',
      'nanosecond'
    ])

    for (const name of fields) {
      if (typeof name !== 'string') throw new TypeError('invalid fields')
      if (!allowed.has(name)) throw new RangeError(`invalid field name ${name}`)
      allowed.delete(name)
      fieldsArray.push(name)
    }

    return calendars.get(this.id).fields(fieldsArray)
  }

  dateFromFields (fields, options) {
    options = options === undefined ? {} : Object.create(options)
    return calendars.get(this.id).dateFromFields(fields, options, this)
  }

  dateAdd (date, duration, options = {}) {
    return calendars.get(this.id).dateAdd(date, duration, options.overflow, this)
  }

  dateUntil (oneParam, twoParam, optionsParam = { largestUnit: 'auto' }) {
    const { years, months, weeks, days } = calendars.get(this.id).dateUntil(
      oneParam,
      twoParam,
      optionsParam
    )

    return new Duration(years, months, weeks, days, 0, 0, 0, 0, 0, 0)
  }

  year (date) {
    return calendars.get(this.id).year(date)
  }

  month (date) {
    return calendars.get(this.id).month(date)
  }

  monthCode (date) {
    return calendars.get(this.id).monthCode(date, this.inLeapYear(date))
  }

  day (date) {
    return calendars.get(this.id).day(date)
  }

  era (date) {
    return calendars.get(this.id).era(date)
  }

  eraYear (date) {
    return calendars.get(this.id).eraYear(date)
  }

  dayOfWeek (date) {
    return calendars.get(this.id).dayOfWeek(date)
  }

  dayOfYear (date) {
    return calendars.get(this.id).dayOfYear(date)
  }

  weekOfYear (date) {
    return calendars.get(this.id).weekOfYear(date)
  }

  daysInWeek (date) {
    return calendars.get(this.id).daysInWeek(date)
  }

  daysInMonth (date) {
    return calendars.get(this.id).daysInMonth(date)
  }

  daysInYear (date) {
    return calendars.get(this.id).daysInYear(date)
  }

  monthsInYear (date) {
    return calendars.get(this.id).monthsInYear(date)
  }

  inLeapYear (date) {
    return calendars.get(this.id).inLeapYear(date)
  }

  toString () {
    return this.#id
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }
}

function yearToISOString (year) {
  const sign = year < 0 ? '-' : '+'

  return sign === '-' || year > 9999
    ? sign + `000000${Math.abs(year)}`.slice(-6)
    : `0000${year}`.slice(-4)
}

function toUtcIsoDateString ({ isoYear, isoMonth, isoDay }) {
  const yearString = yearToISOString(isoYear)
  const monthString = `00${isoMonth}`.slice(-2)
  const dayString = `00${isoDay}`.slice(-2)
  return `${yearString}-${monthString}-${dayString}T00:00Z`
}

function simpleDateDiff (one, two) {
  return {
    years: one.year - two.year,
    months: one.month - two.month,
    days: one.day - two.day
  }
}

function monthCodeNumberPart (monthCode) {
  if (!monthCode.startsWith('M')) {
    throw new RangeError(
      `Invalid month code: ${monthCode}.  Month codes must start with M.`
    )
  }
  const month = +monthCode.slice(1)
  if (isNaN(month)) throw new RangeError(`Invalid month code: ${monthCode}`)
  return month
}

function buildMonthCode (month, leap = false) {
  return `M${month.toString().padStart(2, '0')}${leap ? 'L' : ''}`
}

/**
 * Safely merge a month, monthCode pair into an integer month.
 * If both are present, make sure they match.
 * This logic doesn't work for lunisolar calendars!
 * */
function resolveNonLunisolarMonth (
  calendarDate,
  overflow = undefined,
  monthsPerYear = 12
) {
  let { month, monthCode } = calendarDate
  if (monthCode === undefined) {
    if (month === undefined) throw new TypeError('Either month or monthCode are required')
    if (overflow === 'reject') rejectToRange(month, 1, monthsPerYear)

    if (overflow === 'constrain') {
      month = constrainToRange(month, 1, monthsPerYear)
    }

    monthCode = buildMonthCode(month)
  } else {
    const numberPart = monthCodeNumberPart(monthCode)
    if (month !== undefined && month !== numberPart) {
      throw new RangeError(
        `monthCode ${monthCode} and month ${month} must match if both are present`
      )
    }
    if (monthCode !== buildMonthCode(numberPart)) {
      throw new RangeError(`Invalid month code: ${monthCode}`)
    }
    month = numberPart

    if (month < 1 || month > monthsPerYear) {
      throw new RangeError(`Invalid monthCode: ${monthCode}`)
    }
  }
  return { ...calendarDate, month, monthCode }
}

export function comparisonResult (value) {
  return value < 0 ? -1 : value > 0 ? 1 : value
}

class HelperBase {
  #formatter

  get formatter () {
    return this.#formatter
  }

  getFormatter () {
    if (typeof this.formatter === 'undefined') {
      this.#formatter = new Intl.DateTimeFormat(`en-US-u-ca-${this.id}`, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        era: this.eraLength,
        timeZone: 'UTC'
      })
    }
    return this.formatter
  }

  isoToCalendarDate (isoDate, cache) {
    const { year: isoYear, month: isoMonth, day: isoDay } = isoDate
    const key = JSON.stringify({
      func: 'isoToCalendarDate',
      isoYear,
      isoMonth,
      isoDay,
      id: this.id
    })
    const cached = cache.get(key)
    if (cached) return cached

    const dateTimeFormat = this.getFormatter()
    let parts, isoString
    try {
      isoString = toUtcIsoDateString({ isoYear, isoMonth, isoDay })
      parts = dateTimeFormat.formatToParts(new Date(isoString))
    } catch (e) {
      throw new RangeError(
        `Invalid ISO date: ${JSON.stringify({ isoYear, isoMonth, isoDay })}`
      )
    }
    const result = {}
    for (let { type, value } of parts) {
      if (type === 'year') result.eraYear = Number(value)
      if (type === 'relatedYear') result.eraYear = Number(value)
      if (type === 'month') {
        const matches = /^([0-9]*)(.*?)$/.exec(value)
        if (!matches || matches.length !== 3 || (!matches[1] && !matches[2])) {
          throw new RangeError(`Unexpected month: ${value}`)
        }
        result.month = matches[1] ? +matches[1] : 1
        if (result.month < 1) {
          throw new RangeError(
            `Invalid month ${value} from ${isoString}[u-ca-${this.id}]` +
              ' (probably due to https://bugs.chromium.org/p/v8/issues/detail?id=10527)'
          )
        }
        if (result.month > 13) {
          throw new RangeError(
            `Invalid month ${value} from ${isoString}[u-ca-${this.id}]` +
              ' (probably due to https://bugs.chromium.org/p/v8/issues/detail?id=10529)'
          )
        }

        if (matches[2]) result.monthExtra = matches[2]
      }
      if (type === 'day') result.day = Number(value)
      if (this.hasEra && type === 'era' && value != null && value !== '') {
        value = value.split(' (')[0]
        result.era = value
          .normalize('NFD')
          .replace(/[^-0-9 \p{L}]/gu, '')
          .replace(' ', '-')
          .toLowerCase()
      }
    }
    if (result.eraYear === undefined) {
      throw new RangeError(
        `Intl.DateTimeFormat.formatToParts lacks relatedYear in ${this.id} calendar. Try Node 14+ or modern browsers.`
      )
    }
    // (e.g. Japanese pre-Meiji eras). See https://github.com/tc39/proposal-temporal/issues/526.
    if (this.reviseIntlEra) {
      const { era, eraYear } = this.reviseIntlEra(result, isoDate)
      result.era = era
      result.eraYear = eraYear
    }
    if (this.checkIcuBugs) this.checkIcuBugs(isoDate)

    const calendarDate = this.adjustCalendarDate(
      result,
      cache,
      'constrain',
      true
    )
    if (calendarDate.year === undefined)
      throw new RangeError(`Missing year converting ${JSON.stringify(isoDate)}`)
    if (calendarDate.month === undefined)
      throw new RangeError(
        `Missing month converting ${JSON.stringify(isoDate)}`
      )
    if (calendarDate.day === undefined)
      throw new RangeError(`Missing day converting ${JSON.stringify(isoDate)}`)
    cache.set(key, calendarDate)
    // Also cache the reverse mapping
    ;['constrain', 'reject'].forEach(overflow => {
      const keyReverse = JSON.stringify({
        func: 'calendarToIsoDate',
        year: calendarDate.year,
        month: calendarDate.month,
        day: calendarDate.day,
        overflow,
        id: this.id
      })
      cache.set(keyReverse, isoDate)
    })
    return calendarDate
  }

  validateCalendarDate (calendarDate) {
    const { era, month, year, day, eraYear, monthCode, monthExtra } =
      calendarDate
    // When there's a suffix (e.g. "5bis" for a leap month in Chinese calendar)
    // the derived class must deal with it.
    if (monthExtra !== undefined)
      throw new RangeError('Unexpected `monthExtra` value')
    if (year === undefined && eraYear === undefined)
      throw new TypeError('year or eraYear is required')
    if (month === undefined && monthCode === undefined)
      throw new TypeError('month or monthCode is required')
    if (day === undefined) throw new RangeError('Missing day')
    if (monthCode !== undefined) {
      if (typeof monthCode !== 'string') {
        throw new RangeError(
          `monthCode must be a string, not ${typeof monthCode}`
        )
      }
      if (!/^M([01]?\d)(L?)$/.test(monthCode))
        throw new RangeError(`Invalid monthCode: ${monthCode}`)
    }
    if (this.constantEra) {
      if (era !== undefined && era !== this.constantEra) {
        throw new RangeError(`era must be ${this.constantEra}, not ${era}`)
      }
      if (eraYear !== undefined && year !== undefined && eraYear !== year) {
        throw new RangeError(`eraYear ${eraYear} does not match year ${year}`)
      }
    }
  }

  adjustCalendarDate (
    calendarDateParam,
    cache = undefined,
    overflow = 'constrain',
    fromLegacyDate = false
  ) {
    if (this.calendarType === 'lunisolar') {
      throw new RangeError('Override required for lunisolar calendars')
    }

    let calendarDate = calendarDateParam
    this.validateCalendarDate(calendarDate)
    // For calendars that always use the same era, set it here so that derived
    // calendars won't need to implement this method simply to set the era.
    if (this.constantEra) {
      // year and eraYear always match when there's only one possible era
      const { year, eraYear } = calendarDate
      calendarDate = {
        ...calendarDate,
        era: this.constantEra,
        year: year !== undefined ? year : eraYear,
        eraYear: eraYear !== undefined ? eraYear : year
      }
    }

    const largestMonth = this.monthsInYear(calendarDate, cache)
    let { month, monthCode } = calendarDate

    ;({ month, monthCode } = resolveNonLunisolarMonth(
      calendarDate,
      overflow,
      largestMonth
    ))
    const res = { ...calendarDate, month, monthCode }
    return res
  }

  regulateMonthDayNaive (calendarDate, overflow, cache) {
    const largestMonth = this.monthsInYear(calendarDate, cache)
    let { month, day } = calendarDate
    if (overflow === 'reject') {
      rejectToRange(month, 1, largestMonth)
      rejectToRange(day, 1, this.maximumMonthLength(calendarDate))
    } else {
      month = constrainToRange(month, 1, largestMonth)
      day = constrainToRange(
        day,
        1,
        this.maximumMonthLength({ ...calendarDate, month })
      )
    }
    return { ...calendarDate, month, day }
  }

  calendarToIsoDate (dateParam, overflow = 'constrain', cache) {
    const originalDate = dateParam
    // First, normalize the calendar date to ensure that (year, month, day)
    // are all present, converting monthCode and eraYear if needed.
    let date = this.adjustCalendarDate(dateParam, cache, overflow, false)

    // Fix obviously out-of-bounds values. Values that are valid generally, but
    // not in this particular year, may not be caught here for some calendars.
    // If so, these will be handled lower below.
    date = this.regulateMonthDayNaive(date, overflow, cache)

    const { year, month, day } = date
    const key = JSON.stringify({
      func: 'calendarToIsoDate',
      year,
      month,
      day,
      overflow,
      id: this.id
    })
    let cached = cache.get(key)
    if (cached) return cached
    let keyOriginal
    if (
      originalDate.year !== undefined &&
      originalDate.month !== undefined &&
      originalDate.day !== undefined &&
      (originalDate.year !== date.year ||
        originalDate.month !== date.month ||
        originalDate.day !== date.day)
    ) {
      keyOriginal = JSON.stringify({
        func: 'calendarToIsoDate',
        year: originalDate.year,
        month: originalDate.month,
        day: originalDate.day,
        overflow,
        id: this.id
      })
      cached = cache.get(keyOriginal)
      if (cached) return cached
    }

    let isoEstimate = this.estimateIsoDate({ year, month, day })
    const calculateSameMonthResult = diffDays => {
      let testIsoEstimate = this.addDaysIso(isoEstimate, diffDays)
      if (date.day > this.minimumMonthLength(date)) {
        let testCalendarDate = this.isoToCalendarDate(testIsoEstimate, cache)
        while (
          testCalendarDate.month !== month ||
          testCalendarDate.year !== year
        ) {
          if (overflow === 'reject') {
            throw new RangeError(
              `day ${day} does not exist in month ${month} of year ${year}`
            )
          }
          testIsoEstimate = this.addDaysIso(testIsoEstimate, -1)
          testCalendarDate = this.isoToCalendarDate(testIsoEstimate, cache)
        }
      }
      return testIsoEstimate
    }
    let sign = 0
    let roundtripEstimate = this.isoToCalendarDate(isoEstimate, cache)
    let diff = simpleDateDiff(date, roundtripEstimate)
    if (diff.years !== 0 || diff.months !== 0 || diff.days !== 0) {
      const diffTotalDaysEstimate =
        diff.years * 365 + diff.months * 30 + diff.days
      isoEstimate = this.addDaysIso(isoEstimate, diffTotalDaysEstimate)
      roundtripEstimate = this.isoToCalendarDate(isoEstimate, cache)
      diff = simpleDateDiff(date, roundtripEstimate)
      if (diff.years === 0 && diff.months === 0) {
        isoEstimate = calculateSameMonthResult(diff.days)
      } else {
        sign = this.compareCalendarDates(date, roundtripEstimate)
      }
    }
    let increment = 8
    let maybeConstrained = false
    while (sign) {
      isoEstimate = this.addDaysIso(isoEstimate, sign * increment)
      const oldRoundtripEstimate = roundtripEstimate
      roundtripEstimate = this.isoToCalendarDate(isoEstimate, cache)
      const oldSign = sign
      sign = this.compareCalendarDates(date, roundtripEstimate)
      if (sign) {
        diff = simpleDateDiff(date, roundtripEstimate)
        if (diff.years === 0 && diff.months === 0) {
          isoEstimate = calculateSameMonthResult(diff.days)
          sign = 0
          maybeConstrained = date.day > this.minimumMonthLength(date)
        } else if (oldSign && sign !== oldSign) {
          if (increment > 1) {
            increment /= 2
          } else {
            if (overflow === 'reject') {
              throw new RangeError(
                `Can't find ISO date from calendar date: ${JSON.stringify({
                  ...originalDate
                })}`
              )
            } else {
              const order = this.compareCalendarDates(
                roundtripEstimate,
                oldRoundtripEstimate
              )
              if (order > 0) isoEstimate = this.addDaysIso(isoEstimate, -1)
              maybeConstrained = true
              sign = 0
            }
          }
        }
      }
    }
    cache.set(key, isoEstimate)
    if (keyOriginal) cache.set(keyOriginal, isoEstimate)
    if (
      date.year === undefined ||
      date.month === undefined ||
      date.day === undefined ||
      date.monthCode === undefined ||
      (this.hasEra && (date.era === undefined || date.eraYear === undefined))
    ) {
      throw new RangeError('Unexpected missing property')
    }
    if (!maybeConstrained) {
      const keyReverse = JSON.stringify({
        func: 'isoToCalendarDate',
        isoYear: isoEstimate.year,
        isoMonth: isoEstimate.month,
        isoDay: isoEstimate.day,
        id: this.id
      })
      cache.set(keyReverse, date)
    }
    return isoEstimate
  }

  toCalendarDate (date, cache) {
    const slotMap = SlotMap.get(date)
    const isoDate = { year: slotMap.get('year'), month: slotMap.get('month'), day: slotMap.get('day') }
    const result = this.isoToCalendarDate(isoDate, cache)
    return result
  }

  compareCalendarDates (date1, date2) {
    if (date1.year !== date2.year)
      return comparisonResult(date1.year - date2.year)
    if (date1.month !== date2.month)
      return comparisonResult(date1.month - date2.month)
    if (date1.day !== date2.day) return comparisonResult(date1.day - date2.day)
    return 0
  }

  regulateDate (calendarDate, overflow = 'constrain', cache) {
    const isoDate = this.calendarToIsoDate(calendarDate, overflow, cache)
    return this.isoToCalendarDate(isoDate, cache)
  }

  addDaysIso (isoDate, days) {
    const added = addISODate(
      isoDate.year,
      isoDate.month,
      isoDate.day,
      0,
      0,
      0,
      days,
      'constrain'
    )
    return added
  }

  addDaysCalendar (calendarDate, days, cache) {
    const isoDate = this.calendarToIsoDate(calendarDate, 'constrain', cache)
    const addedIso = this.addDaysIso(isoDate, days)
    const addedCalendar = this.isoToCalendarDate(addedIso, cache)
    return addedCalendar
  }

  addMonthsCalendar (calendarDateParam, months, overflow, cache) {
    let calendarDate = calendarDateParam
    const { day } = calendarDate
    for (let i = 0, absMonths = Math.abs(months); i < absMonths; i++) {
      const { month } = calendarDate
      const oldCalendarDate = calendarDate
      const days =
        months < 0
          ? -Math.max(day, this.daysInPreviousMonth(calendarDate, cache))
          : this.daysInMonth(calendarDate, cache)
      const isoDate = this.calendarToIsoDate(calendarDate, 'constrain', cache)
      let addedIso = this.addDaysIso(isoDate, days)
      calendarDate = this.isoToCalendarDate(addedIso, cache)

      if (months > 0) {
        const monthsInOldYear = this.monthsInYear(oldCalendarDate, cache)
        while (calendarDate.month - 1 !== month % monthsInOldYear) {
          addedIso = this.addDaysIso(addedIso, -1)
          calendarDate = this.isoToCalendarDate(addedIso, cache)
        }
      }

      if (calendarDate.day !== day) {
        calendarDate = this.regulateDate(
          { ...calendarDate, day },
          'constrain',
          cache
        )
      }
    }
    if (overflow === 'reject' && calendarDate.day !== day) {
      throw new RangeError(
        `Day ${day} does not exist in resulting calendar month`
      )
    }
    return calendarDate
  }

  addCalendar (
    calendarDate,
    { years = 0, months = 0, weeks = 0, days = 0 },
    overflow,
    cache
  ) {
    const { year, day, monthCode } = calendarDate
    const addedYears = this.adjustCalendarDate(
      { year: year + years, monthCode, day },
      cache
    )
    const addedMonths = this.addMonthsCalendar(
      addedYears,
      months,
      overflow,
      cache
    )
    const initialDays = days + weeks * 7
    const addedDays = this.addDaysCalendar(addedMonths, initialDays, cache)
    return addedDays
  }

  untilCalendar (calendarOne, calendarTwo, largestUnit, cache) {
    let days = 0
    let weeks = 0
    let months = 0
    let years = 0
    largestUnit =
      typeof largestUnit !== 'string' || largestUnit === 'auto'
        ? 'day'
        : largestUnit
    switch (largestUnit) {
      case 'day':
        days = this.calendarDaysUntil(calendarOne, calendarTwo, cache)
        break
      case 'week': {
        const totalDays = this.calendarDaysUntil(
          calendarOne,
          calendarTwo,
          cache
        )
        days = totalDays % 7
        weeks = (totalDays - days) / 7
        break
      }
      case 'month':
      case 'year': {
        const diffYears = calendarTwo.year - calendarOne.year
        const diffMonths = calendarTwo.month - calendarOne.month
        const diffDays = calendarTwo.day - calendarOne.day
        const sign = this.compareCalendarDates(calendarTwo, calendarOne)
        if (!sign) {
          return { years: 0, months: 0, weeks: 0, days: 0 }
        }
        if (largestUnit === 'year' && diffYears) {
          const isOneFurtherInYear =
            diffMonths * sign < 0 || (diffMonths === 0 && diffDays * sign < 0)
          years = isOneFurtherInYear ? diffYears - sign : diffYears
        }
        const yearsAdded = years
          ? this.addCalendar(calendarOne, { years }, 'constrain', cache)
          : calendarOne
        let current
        let next = yearsAdded
        do {
          months += sign
          current = next
          next = this.addMonthsCalendar(current, sign, 'constrain', cache)
          if (next.day !== calendarOne.day) {
            next = this.regulateDate(
              { ...next, day: calendarOne.day },
              'constrain',
              cache
            )
          }
        } while (this.compareCalendarDates(calendarTwo, next) * sign >= 0)
        months -= sign
        const remainingDays = this.calendarDaysUntil(
          current,
          calendarTwo,
          cache
        )
        days = remainingDays
        break
      }
    }

    return { years, months, weeks, days }
  }

  daysInMonth (calendarDate, cache) {
    const { day } = calendarDate
    const max = this.maximumMonthLength(calendarDate)
    const min = this.minimumMonthLength(calendarDate)
    if (min === max) return min

    const increment = day <= max - min ? max : min
    const isoDate = this.calendarToIsoDate(calendarDate, 'constrain', cache)
    const addedIsoDate = this.addDaysIso(isoDate, increment)
    const addedCalendarDate = this.isoToCalendarDate(addedIsoDate, cache)

    const endOfMonthIso = this.addDaysIso(addedIsoDate, -addedCalendarDate.day)
    const endOfMonthCalendar = this.isoToCalendarDate(endOfMonthIso, cache)
    return endOfMonthCalendar.day
  }

  daysInPreviousMonth (calendarDate, cache) {
    const { day, month, year } = calendarDate

    const previousMonthYear = month > 1 ? year : year - 1
    let previousMonthDate = { year: previousMonthYear, month, day: 1 }
    const previousMonth =
      month > 1 ? month - 1 : this.monthsInYear(previousMonthDate, cache)
    previousMonthDate = { ...previousMonthDate, month: previousMonth }
    const min = this.minimumMonthLength(previousMonthDate)
    const max = this.maximumMonthLength(previousMonthDate)
    if (min === max) return max

    const isoDate = this.calendarToIsoDate(calendarDate, 'constrain', cache)
    const lastDayOfPreviousMonthIso = this.addDaysIso(isoDate, -day)
    const lastDayOfPreviousMonthCalendar = this.isoToCalendarDate(
      lastDayOfPreviousMonthIso,
      cache
    )
    return lastDayOfPreviousMonthCalendar.day
  }

  startOfCalendarYear (calendarDate) {
    return { year: calendarDate.year, month: 1, monthCode: 'M01', day: 1 }
  }

  startOfCalendarMonth (calendarDate) {
    return { year: calendarDate.year, month: calendarDate.month, day: 1 }
  }

  calendarDaysUntil (calendarOne, calendarTwo, cache) {
    const oneIso = this.calendarToIsoDate(calendarOne, 'constrain', cache)
    const twoIso = this.calendarToIsoDate(calendarTwo, 'constrain', cache)
    return this.isoDaysUntil(oneIso, twoIso)
  }

  isoDaysUntil (oneIso, twoIso) {
    const duration = differenceISODate(
      oneIso.year,
      oneIso.month,
      oneIso.day,
      twoIso.year,
      twoIso.month,
      twoIso.day,
      'day'
    )
    return duration.days
  }

  eraLength = 'short'
  hasEra = true

  monthDayFromFields (fields, overflow, cache) {
    let { year, month, monthCode, day, era, eraYear } = fields
    if (monthCode === undefined) {
      if (year === undefined && (era === undefined || eraYear === undefined)) {
        throw new TypeError(
          '`monthCode`, `year`, or `era` and `eraYear` is required'
        )
      }
      ;({ monthCode, year } = this.adjustCalendarDate(
        { year, month, monthCode, day, era, eraYear },
        cache,
        overflow
      ))
    }

    let isoYear, isoMonth, isoDay
    let closestCalendar, closestIso
    const startDateIso = { year: 1972, month: 1, day: 1 }
    const { year: calendarYear } = this.isoToCalendarDate(startDateIso, cache)
    for (let i = 0; i < 100; i++) {
      const testCalendarDate = this.adjustCalendarDate(
        { day, monthCode, year: calendarYear - i },
        cache
      )
      const isoDate = this.calendarToIsoDate(
        testCalendarDate,
        'constrain',
        cache
      )
      const roundTripCalendarDate = this.isoToCalendarDate(isoDate, cache)
      ;({ year: isoYear, month: isoMonth, day: isoDay } = isoDate)
      if (
        roundTripCalendarDate.monthCode === monthCode &&
        roundTripCalendarDate.day === day
      ) {
        return { month: isoMonth, day: isoDay, year: isoYear }
      } else if (overflow === 'constrain') {
        if (
          closestCalendar === undefined ||
          (roundTripCalendarDate.monthCode === closestCalendar.monthCode &&
            roundTripCalendarDate.day > closestCalendar.day)
        ) {
          closestCalendar = roundTripCalendarDate
          closestIso = isoDate
        }
      }
    }
    if (overflow === 'constrain' && closestIso !== undefined) return closestIso
    throw new RangeError(
      `No recent ${this.id} year with monthCode ${monthCode} and day ${day}`
    )
  }
}

class HebrewHelper extends HelperBase {
  id = 'hebrew'
  calendarType = 'lunisolar'

  inLeapYear (calendarDate) {
    const { year } = calendarDate
    return (7 * year + 1) % 19 < 7
  }

  monthsInYear (calendarDate) {
    return this.inLeapYear(calendarDate) ? 13 : 12
  }

  minimumMonthLength (calendarDate) {
    return this.minMaxMonthLength(calendarDate, 'min')
  }

  maximumMonthLength (calendarDate) {
    return this.minMaxMonthLength(calendarDate, 'max')
  }

  minMaxMonthLength (calendarDate, minOrMax) {
    const { month, year } = calendarDate
    const monthCode = this.getMonthCode(year, month)
    const monthInfo = Object.entries(this.months).find(
      m => m[1].monthCode === monthCode
    )
    if (monthInfo === undefined)
      throw new RangeError(`unmatched Hebrew month: ${month}`)
    const daysInMonth = monthInfo[1].days
    return typeof daysInMonth === 'number' ? daysInMonth : daysInMonth[minOrMax]
  }

  estimateIsoDate (calendarDate) {
    const { year } = calendarDate
    return { year: year - 3760, month: 1, day: 1 }
  }

  months = {
    Tishri: { leap: 1, regular: 1, monthCode: 'M01', days: 30 },
    Heshvan: {
      leap: 2,
      regular: 2,
      monthCode: 'M02',
      days: { min: 29, max: 30 }
    },
    Kislev: {
      leap: 3,
      regular: 3,
      monthCode: 'M03',
      days: { min: 29, max: 30 }
    },
    Tevet: { leap: 4, regular: 4, monthCode: 'M04', days: 29 },
    Shevat: { leap: 5, regular: 5, monthCode: 'M05', days: 30 },
    Adar: { leap: undefined, regular: 6, monthCode: 'M06', days: 29 },
    'Adar I': { leap: 6, regular: undefined, monthCode: 'M05L', days: 30 },
    'Adar II': { leap: 7, regular: undefined, monthCode: 'M06', days: 29 },
    Nisan: { leap: 8, regular: 7, monthCode: 'M07', days: 30 },
    Iyar: { leap: 9, regular: 8, monthCode: 'M08', days: 29 },
    Sivan: { leap: 10, regular: 9, monthCode: 'M09', days: 30 },
    Tamuz: { leap: 11, regular: 10, monthCode: 'M10', days: 29 },
    Av: { leap: 12, regular: 11, monthCode: 'M11', days: 30 },
    Elul: { leap: 13, regular: 12, monthCode: 'M12', days: 29 }
  }

  getMonthCode (year, month) {
    if (this.inLeapYear({ year })) {
      return month === 6
        ? buildMonthCode(5, true)
        : buildMonthCode(month < 6 ? month : month - 1)
    } else {
      return buildMonthCode(month)
    }
  }

  adjustCalendarDate (
    calendarDate,
    cache,
    overflow = 'constrain',
    fromLegacyDate = false
  ) {
    let { year, eraYear, month, monthCode, day, monthExtra } = calendarDate
    if (year === undefined && eraYear !== undefined) year = eraYear
    if (eraYear === undefined && year !== undefined) eraYear = year
    if (fromLegacyDate) {
      if (monthExtra) {
        const monthInfo = this.months[monthExtra]
        if (!monthInfo)
          throw new RangeError(
            `Unrecognized month from formatToParts: ${monthExtra}`
          )
        month = this.inLeapYear({ year }) ? monthInfo.leap : monthInfo.regular
      }
      // Because we're getting data from legacy Date, then `month` will always be present
      monthCode = this.getMonthCode(year, month)
      const result = {
        year,
        month: month,
        day,
        era: undefined,
        eraYear,
        monthCode
      }
      return result
    } else {
      this.validateCalendarDate(calendarDate)
      if (month === undefined) {
        if (monthCode.endsWith('L')) {
          if (monthCode !== 'M05L') {
            throw new RangeError(
              `Hebrew leap month must have monthCode M05L, not ${monthCode}`
            )
          }
          month = 6
          if (!this.inLeapYear({ year })) {
            if (overflow === 'reject') {
              throw new RangeError(
                `Hebrew monthCode M05L is invalid in year ${year} which is not a leap year`
              )
            } else {
              month = 5
              day = 30
              monthCode = 'M05'
            }
          }
        } else {
          month = monthCodeNumberPart(monthCode)
          if (this.inLeapYear({ year }) && month >= 6) month++
          const largestMonth = this.monthsInYear({ year })
          if (month < 1 || month > largestMonth)
            throw new RangeError(`Invalid monthCode: ${monthCode}`)
        }
      } else {
        if (overflow === 'reject') {
          rejectToRange(month, 1, this.monthsInYear({ year }))
          rejectToRange(day, 1, this.maximumMonthLength({ year, month }))
        } else {
          month = constrainToRange(month, 1, this.monthsInYear({ year }))
          day = constrainToRange(
            day,
            1,
            this.maximumMonthLength({ year, month })
          )
        }
        if (monthCode === undefined) {
          monthCode = this.getMonthCode(year, month)
        } else {
          const calculatedMonthCode = this.getMonthCode(year, month)
          if (calculatedMonthCode !== monthCode) {
            throw new RangeError(
              `monthCode ${monthCode} doesn't correspond to month ${month} in Hebrew year ${year}`
            )
          }
        }
      }
      return { ...calendarDate, day, month, monthCode, year, eraYear }
    }
  }

  hasEra = false
}

class IslamicBaseHelper extends HelperBase {
  calendarType = 'lunar'

  inLeapYear (calendarDate, cache) {
    const days = this.daysInMonth(
      { year: calendarDate.year, month: 12, day: 1 },
      cache
    )
    return days === 30
  }

  monthsInYear () {
    return 12
  }

  minimumMonthLength () {
    return 29
  }

  maximumMonthLength () {
    return 30
  }

  DAYS_PER_ISLAMIC_YEAR = 354 + 11 / 30
  DAYS_PER_ISO_YEAR = 365.2425
  constantEra = 'ah'

  estimateIsoDate (calendarDate) {
    const { year } = this.adjustCalendarDate(calendarDate)
    return {
      year:
        Math.floor(
          (year * this.DAYS_PER_ISLAMIC_YEAR) / this.DAYS_PER_ISO_YEAR
        ) + 622,
      month: 1,
      day: 1
    }
  }
}

class IslamicHelper extends IslamicBaseHelper {
  id = 'islamic'
}

class IslamicUmalquraHelper extends IslamicBaseHelper {
  id = 'islamic-umalqura'
}

class IslamicTblaHelper extends IslamicBaseHelper {
  id = 'islamic-tbla'
}

class IslamicCivilHelper extends IslamicBaseHelper {
  id = 'islamic-civil'
}

class IslamicRgsaHelper extends IslamicBaseHelper {
  id = 'islamic-rgsa'
}

class IslamicCcHelper extends IslamicBaseHelper {
  id = 'islamicc'
}

class PersianHelper extends HelperBase {
  id = 'persian'
  calendarType = 'solar'

  inLeapYear (calendarDate, cache) {
    return IslamicHelper.prototype.inLeapYear.call(this, calendarDate, cache)
  }

  monthsInYear (/* calendarYear, cache */) {
    return 12
  }

  minimumMonthLength (calendarDate) {
    const { month } = calendarDate
    if (month === 12) return 29
    return month <= 6 ? 31 : 30
  }

  maximumMonthLength (calendarDate) {
    const { month } = calendarDate
    if (month === 12) return 30
    return month <= 6 ? 31 : 30
  }

  constantEra = 'ap'

  estimateIsoDate (calendarDate) {
    const { year } = this.adjustCalendarDate(calendarDate)
    return { year: year + 621, month: 1, day: 1 }
  }
}

function isGregorianLeapYear (year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

class IndianHelper extends HelperBase {
  id = 'indian'
  calendarType = 'solar'

  inLeapYear (calendarDate) {
    return isGregorianLeapYear(calendarDate.year + 78)
  }

  monthsInYear (/* calendarYear, cache */) {
    return 12
  }

  minimumMonthLength (calendarDate) {
    return this.getMonthInfo(calendarDate).length
  }

  maximumMonthLength (calendarDate) {
    return this.getMonthInfo(calendarDate).length
  }

  constantEra = 'saka'
  months = {
    1: {
      length: 30,
      month: 3,
      day: 22,
      leap: { length: 31, month: 3, day: 21 }
    },
    2: { length: 31, month: 4, day: 21 },
    3: { length: 31, month: 5, day: 22 },
    4: { length: 31, month: 6, day: 22 },
    5: { length: 31, month: 7, day: 23 },
    6: { length: 31, month: 8, day: 23 },
    7: { length: 30, month: 9, day: 23 },
    8: { length: 30, month: 10, day: 23 },
    9: { length: 30, month: 11, day: 22 },
    10: { length: 30, month: 12, day: 22 },
    11: { length: 30, month: 1, nextYear: true, day: 21 },
    12: { length: 30, month: 2, nextYear: true, day: 20 }
  }

  getMonthInfo (calendarDate) {
    const { month } = calendarDate
    let monthInfo = this.months[month]
    if (monthInfo === undefined) throw new RangeError(`Invalid month: ${month}`)
    if (this.inLeapYear(calendarDate) && monthInfo.leap)
      monthInfo = monthInfo.leap
    return monthInfo
  }

  estimateIsoDate (calendarDateParam) {
    const calendarDate = this.adjustCalendarDate(calendarDateParam)
    const monthInfo = this.getMonthInfo(calendarDate)
    const isoYear = calendarDate.year + 78 + (monthInfo.nextYear ? 1 : 0)
    const isoMonth = monthInfo.month
    const isoDay = monthInfo.day
    const isoDate = addISODate(
      isoYear,
      isoMonth,
      isoDay,
      0,
      0,
      0,
      calendarDate.day - 1,
      'constrain'
    )
    return isoDate
  }

  vulnerableToBceBug =
    new Date('0000-01-01T00:00Z').toLocaleDateString('en-US-u-ca-indian', {
      timeZone: 'UTC'
    }) !== '10/11/-79 Saka'

  checkIcuBugs (isoDate) {
    if (this.vulnerableToBceBug && isoDate.year < 1) {
      throw new RangeError(
        `calendar '${this.id}' is broken for ISO dates before 0001-01-01` +
          ' (see https://bugs.chromium.org/p/v8/issues/detail?id=10529)'
      )
    }
  }
}

function adjustEras (erasParam) {
  let eras = erasParam
  if (eras.length === 0) {
    throw new RangeError('Invalid era data: eras are required')
  }
  if (eras.length === 1 && eras[0].reverseOf) {
    throw new RangeError(
      'Invalid era data: anchor era cannot count years backwards'
    )
  }
  if (eras.length === 1 && !eras[0].name) {
    throw new RangeError('Invalid era data: at least one named era is required')
  }
  if (eras.filter(e => e.reverseOf != null).length > 1) {
    throw new RangeError(
      'Invalid era data: only one era can count years backwards'
    )
  }

  let anchorEra
  eras.forEach(e => {
    if (e.isAnchor || (!e.anchorEpoch && !e.reverseOf)) {
      if (anchorEra)
        throw new RangeError(
          'Invalid era data: cannot have multiple anchor eras'
        )
      anchorEra = e
      e.anchorEpoch = { year: e.hasYearZero ? 0 : 1 }
    } else if (!e.name) {
      throw new RangeError('If era name is blank, it must be the anchor era')
    }
  })

  eras = eras.filter(e => e.name)

  eras.forEach(e => {
    const { reverseOf } = e
    if (reverseOf) {
      const reversedEra = eras.find(era => era.name === reverseOf)
      if (reversedEra === undefined)
        throw new RangeError(
          `Invalid era data: unmatched reverseOf era: ${reverseOf}`
        )
      e.reverseOf = reversedEra
      e.anchorEpoch = reversedEra.anchorEpoch
      e.isoEpoch = reversedEra.isoEpoch
    }
    if (e.anchorEpoch.month === undefined) e.anchorEpoch.month = 1
    if (e.anchorEpoch.day === undefined) e.anchorEpoch.day = 1
  })

  Array.prototype.sort.call(eras, (e1, e2) => {
    if (e1.reverseOf) return 1
    if (e2.reverseOf) return -1
    if (!e1.isoEpoch || !e2.isoEpoch)
      throw new RangeError('Invalid era data: missing ISO epoch')
    return e2.isoEpoch.year - e1.isoEpoch.year
  })

  const lastEraReversed = eras[eras.length - 1].reverseOf
  if (lastEraReversed) {
    if (lastEraReversed !== eras[eras.length - 2])
      throw new RangeError('Invalid era data: invalid reverse-sign era')
  }

  eras.forEach((e, i) => {
    e.genericName = `era${eras.length - 1 - i}`
  })

  return { eras: eras, anchorEra: anchorEra || eras[0] }
}

class GregorianBaseHelper extends HelperBase {
  id
  eras
  anchorEra

  constructor (id, originalEras) {
    super()
    this.id = id
    const { eras, anchorEra } = adjustEras(originalEras)
    this.anchorEra = anchorEra
    this.eras = eras
  }

  calendarType = 'solar'

  inLeapYear (calendarDate) {
    const { year } = this.estimateIsoDate({
      month: 1,
      day: 1,
      year: calendarDate.year
    })
    return isGregorianLeapYear(year)
  }

  monthsInYear (/* calendarDate */) {
    return 12
  }

  minimumMonthLength (calendarDate) {
    const { month } = calendarDate
    if (month === 2) return this.inLeapYear(calendarDate) ? 29 : 28
    return [4, 6, 9, 11].indexOf(month) >= 0 ? 30 : 31
  }

  maximumMonthLength (calendarDate) {
    return this.minimumMonthLength(calendarDate)
  }

  /** Fill in missing parts of the (year, era, eraYear) tuple */
  completeEraYear (calendarDate) {
    const checkField = (name, value) => {
      const currentValue = calendarDate[name]
      if (currentValue != null && currentValue !== value) {
        throw new RangeError(
          `Input ${name} ${currentValue} doesn't match calculated value ${value}`
        )
      }
    }
    const eraFromYear = year => {
      let eraYear
      const adjustedCalendarDate = { ...calendarDate, year }
      const matchingEra = this.eras.find((e, i) => {
        if (i === this.eras.length - 1) {
          if (e.reverseOf) {
            if (year > 0)
              throw new RangeError(
                `Signed year ${year} is invalid for era ${e.name}`
              )
            eraYear = e.anchorEpoch.year - year
            return true
          }
          eraYear = year - e.anchorEpoch.year + (e.hasYearZero ? 0 : 1)
          return true
        }
        const comparison = this.compareCalendarDates(
          adjustedCalendarDate,
          e.anchorEpoch
        )
        if (comparison >= 0) {
          eraYear = year - e.anchorEpoch.year + (e.hasYearZero ? 0 : 1)
          return true
        }
        return false
      })
      if (!matchingEra)
        throw new RangeError(`Year ${year} was not matched by any era`)
      return { eraYear: eraYear, era: matchingEra.name }
    }

    let { year, eraYear, era } = calendarDate
    if (year != null) {
      ;({ eraYear, era } = eraFromYear(year))
      checkField('era', era)
      checkField('eraYear', eraYear)
    } else if (eraYear != null) {
      const matchingEra =
        era === undefined
          ? undefined
          : this.eras.find(e => e.name === era || e.genericName === era)
      if (!matchingEra)
        throw new RangeError(
          `Era ${era} (ISO year ${eraYear}) was not matched by any era`
        )
      if (eraYear < 1 && matchingEra.reverseOf) {
        throw new RangeError(
          `Years in ${era} era must be positive, not ${year}`
        )
      }
      if (matchingEra.reverseOf) {
        year = matchingEra.anchorEpoch.year - eraYear
      } else {
        year =
          eraYear +
          matchingEra.anchorEpoch.year -
          (matchingEra.hasYearZero ? 0 : 1)
      }
      checkField('year', year)
      ;({ eraYear, era } = eraFromYear(year))
    } else {
      throw new RangeError('Either `year` or `eraYear` and `era` are required')
    }
    return { ...calendarDate, year, eraYear, era }
  }

  adjustCalendarDate (calendarDateParam, cache, overflow = 'constrain') {
    let calendarDate = calendarDateParam
    const { month, monthCode } = calendarDate
    if (month === undefined)
      calendarDate = { ...calendarDate, month: monthCodeNumberPart(monthCode) }
    this.validateCalendarDate(calendarDate)
    calendarDate = this.completeEraYear(calendarDate)
    return super.adjustCalendarDate(calendarDate, cache, overflow)
  }

  estimateIsoDate (calendarDateParam) {
    const calendarDate = this.adjustCalendarDate(calendarDateParam)
    const { year, month, day } = calendarDate
    const { anchorEra } = this
    const isoYearEstimate =
      year + anchorEra.isoEpoch.year - (anchorEra.hasYearZero ? 0 : 1)
    return regulateISODate(isoYearEstimate, month, day, 'constrain')
  }

  v8IsVulnerableToJulianBug = new Date('+001001-01-01T00:00Z')
    .toLocaleDateString('en-US-u-ca-japanese', { timeZone: 'UTC' })
    .startsWith('12')

  calendarIsVulnerableToJulianBug = false

  checkIcuBugs (isoDate) {
    if (
      this.calendarIsVulnerableToJulianBug &&
      this.v8IsVulnerableToJulianBug
    ) {
      const beforeJulianSwitch =
        compareISODate(isoDate.year, isoDate.month, isoDate.day, 1582, 10, 15) <
        0
      if (beforeJulianSwitch) {
        throw new RangeError(
          `calendar '${this.id}' is broken for ISO dates before 1582-10-15` +
            ' (see https://bugs.chromium.org/p/chromium/issues/detail?id=1173158)'
        )
      }
    }
  }
}

const compareAB = (a, b) => (a < b ? -1 : a > b ? 1 : 0)

function compareISODate (y1, m1, d1, y2, m2, d2) {
  let res = 0
  if ((res = compareAB(y1, y2)) !== 0) return res
  if ((res = compareAB(m1, m2)) !== 0) return res
  if ((res = compareAB(d1, d2)) !== 0) return res
  return 0
}

class OrthodoxBaseHelper extends GregorianBaseHelper {
  constructor (id, originalEras) {
    super(id, originalEras)
  }

  inLeapYear (calendarDate) {
    const { year } = calendarDate
    return (year + 1) % 4 === 0
  }

  monthsInYear (/* calendarDate */) {
    return 13
  }

  minimumMonthLength (calendarDate) {
    const { month } = calendarDate
    if (month === 13) return this.inLeapYear(calendarDate) ? 6 : 5
    return 30
  }

  maximumMonthLength (calendarDate) {
    return this.minimumMonthLength(calendarDate)
  }
}

class EthioaaHelper extends OrthodoxBaseHelper {
  constructor () {
    super('ethioaa', [
      { name: 'era0', isoEpoch: { year: -5492, month: 7, day: 17 } }
    ])
  }
}

class CopticHelper extends OrthodoxBaseHelper {
  constructor () {
    super('coptic', [
      { name: 'era1', isoEpoch: { year: 284, month: 8, day: 29 } },
      { name: 'era0', reverseOf: 'era1' }
    ])
  }
}

class EthiopicHelper extends OrthodoxBaseHelper {
  constructor () {
    super('ethiopic', [
      { name: 'era0', isoEpoch: { year: -5492, month: 7, day: 17 } },
      {
        name: 'era1',
        isoEpoch: { year: 8, month: 8, day: 27 },
        anchorEpoch: { year: 5501 }
      }
    ])
  }
}

class RocHelper extends GregorianBaseHelper {
  constructor () {
    super('roc', [
      { name: 'minguo', isoEpoch: { year: 1912, month: 1, day: 1 } },
      { name: 'before-roc', reverseOf: 'minguo' }
    ])
  }

  calendarIsVulnerableToJulianBug = true
}

class BuddhistHelper extends GregorianBaseHelper {
  constructor () {
    super('buddhist', [
      {
        name: 'be',
        hasYearZero: true,
        isoEpoch: { year: -543, month: 1, day: 1 }
      }
    ])
  }

  calendarIsVulnerableToJulianBug = true
}

class GregoryHelper extends GregorianBaseHelper {
  constructor () {
    super('gregory', [
      { name: 'ce', isoEpoch: { year: 1, month: 1, day: 1 } },
      { name: 'bce', reverseOf: 'ce' }
    ])
  }

  reviseIntlEra (calendarDate /*, isoDate: IsoDate */) {
    let { era, eraYear } = calendarDate
    if (era === 'bc' || era === 'b') era = 'bce'
    if (era === 'ad' || era === 'a') era = 'ce'
    return { era, eraYear }
  }
}

class JapaneseHelper extends GregorianBaseHelper {
  constructor () {
    super('japanese', [
      {
        name: 'reiwa',
        isoEpoch: { year: 2019, month: 5, day: 1 },
        anchorEpoch: { year: 2019, month: 5, day: 1 }
      },
      {
        name: 'heisei',
        isoEpoch: { year: 1989, month: 1, day: 8 },
        anchorEpoch: { year: 1989, month: 1, day: 8 }
      },
      {
        name: 'showa',
        isoEpoch: { year: 1926, month: 12, day: 25 },
        anchorEpoch: { year: 1926, month: 12, day: 25 }
      },
      {
        name: 'taisho',
        isoEpoch: { year: 1912, month: 7, day: 30 },
        anchorEpoch: { year: 1912, month: 7, day: 30 }
      },
      {
        name: 'meiji',
        isoEpoch: { year: 1868, month: 9, day: 8 },
        anchorEpoch: { year: 1868, month: 9, day: 8 }
      },
      { name: 'ce', isoEpoch: { year: 1, month: 1, day: 1 } },
      { name: 'bce', reverseOf: 'ce' }
    ])
  }

  calendarIsVulnerableToJulianBug = true
  eraLength = 'long'

  reviseIntlEra (calendarDate, isoDate) {
    const { era, eraYear } = calendarDate
    const { year: isoYear } = isoDate
    if (this.eras.find(e => e.name === era)) return { era, eraYear }
    return isoYear < 1
      ? { era: 'bce', eraYear: 1 - isoYear }
      : { era: 'ce', eraYear: isoYear }
  }
}

class ChineseBaseHelper extends HelperBase {
  id
  calendarType = 'lunisolar'
  inLeapYear (calendarDate, cache) {
    const months = this.getMonthList(calendarDate.year, cache)
    return Object.entries(months).length === 13
  }

  monthsInYear (calendarDate, cache) {
    return this.inLeapYear(calendarDate, cache) ? 13 : 12
  }

  minimumMonthLength (/* calendarDate */) {
    return 29
  }

  maximumMonthLength (/* calendarDate */) {
    return 30
  }

  getMonthList (calendarYear, cache) {
    if (calendarYear === undefined) {
      throw new TypeError('Missing year')
    }
    const key = JSON.stringify({
      func: 'getMonthList',
      calendarYear,
      id: this.id
    })
    const cached = cache.get(key)
    if (cached) return cached
    const dateTimeFormat = this.getFormatter()
    const getCalendarDate = (isoYear, daysPastFeb1) => {
      const isoStringFeb1 = toUtcIsoDateString({
        isoYear,
        isoMonth: 2,
        isoDay: 1
      })
      const legacyDate = new Date(isoStringFeb1)
      legacyDate.setUTCDate(daysPastFeb1 + 1)
      const newYearGuess = dateTimeFormat.formatToParts(legacyDate)
      const calendarMonthString = newYearGuess.find(
        tv => tv.type === 'month'
      ).value
      const calendarDay = +newYearGuess.find(tv => tv.type === 'day').value
      let calendarYearToVerify = newYearGuess.find(
        tv => tv.type === 'relatedYear'
      )
      if (calendarYearToVerify !== undefined) {
        calendarYearToVerify = +calendarYearToVerify.value
      } else {
        throw new RangeError(
          `Intl.DateTimeFormat.formatToParts lacks relatedYear in ${this.id} calendar. Try Node 14+ or modern browsers.`
        )
      }
      return { calendarMonthString, calendarDay, calendarYearToVerify }
    }

    let isoDaysDelta = 17
    let { calendarMonthString, calendarDay, calendarYearToVerify } =
      getCalendarDate(calendarYear, isoDaysDelta)

    if (calendarMonthString !== '1') {
      isoDaysDelta += 29
      ;({ calendarMonthString, calendarDay } = getCalendarDate(
        calendarYear,
        isoDaysDelta
      ))
    }

    isoDaysDelta -= calendarDay - 5
    const result = {}
    let monthIndex = 1
    let oldCalendarDay
    let oldMonthString
    let done = false
    do {
      ;({ calendarMonthString, calendarDay, calendarYearToVerify } =
        getCalendarDate(calendarYear, isoDaysDelta))
      if (oldCalendarDay) {
        result[oldMonthString].daysInMonth = oldCalendarDay + 30 - calendarDay
      }
      if (calendarYearToVerify !== calendarYear) {
        done = true
      } else {
        result[calendarMonthString] = { monthIndex: monthIndex++ }
        isoDaysDelta += 30
      }
      oldCalendarDay = calendarDay
      oldMonthString = calendarMonthString
    } while (!done)
    result[oldMonthString].daysInMonth = oldCalendarDay + 30 - calendarDay

    cache.set(key, result)
    return result
  }

  estimateIsoDate (calendarDate) {
    const { year, month } = calendarDate
    return { year, month: month >= 12 ? 12 : month + 1, day: 1 }
  }

  adjustCalendarDate (
    calendarDate,
    cache,
    overflow = 'constrain',
    fromLegacyDate = false
  ) {
    let { year, month, monthExtra, day, monthCode, eraYear } = calendarDate
    if (fromLegacyDate) {
      year = eraYear
      if (monthExtra && monthExtra !== 'bis')
        throw new RangeError(`Unexpected leap month suffix: ${monthExtra}`)
      const monthCode = buildMonthCode(month, monthExtra !== undefined)
      const monthString = `${month}${monthExtra || ''}`
      const months = this.getMonthList(year, cache)
      const monthInfo = months[monthString]
      if (monthInfo === undefined)
        throw new RangeError(
          `Unmatched month ${monthString} in Chinese year ${year}`
        )
      month = monthInfo.monthIndex
      return { year: year, month, day: day, era: undefined, eraYear, monthCode }
    } else {
      this.validateCalendarDate(calendarDate)
      if (year === undefined) year = eraYear
      if (eraYear === undefined) eraYear = year
      if (month === undefined) {
        const months = this.getMonthList(year, cache)
        let numberPart = monthCode.replace('L', 'bis').slice(1)
        if (numberPart[0] === '0') numberPart = numberPart.slice(1)
        let monthInfo = months[numberPart]
        month = monthInfo && monthInfo.monthIndex
        if (
          month === undefined &&
          monthCode.endsWith('L') &&
          !ArrayIncludes.call(['M01L', 'M12L', 'M13L'], monthCode) &&
          overflow === 'constrain'
        ) {
          let withoutML = monthCode.slice(1, -1)
          if (withoutML[0] === '0') withoutML = withoutML.slice(1)
          monthInfo = months[withoutML]
          if (monthInfo) {
            ;({ daysInMonth: day, monthIndex: month } = monthInfo)
            monthCode = buildMonthCode(withoutML)
          }
        }
        if (month === undefined) {
          throw new RangeError(
            `Unmatched month ${monthCode} in Chinese year ${year}`
          )
        }
      } else if (monthCode === undefined) {
        const months = this.getMonthList(year, cache)
        const monthEntries = Object.entries(months)
        const largestMonth = monthEntries.length
        if (overflow === 'reject') {
          rejectToRange(month, 1, largestMonth)
          rejectToRange(day, 1, this.maximumMonthLength())
        } else {
          month = constrainToRange(month, 1, largestMonth)
          day = constrainToRange(day, 1, this.maximumMonthLength())
        }
        const matchingMonthEntry = monthEntries.find(
          ([, v]) => v.monthIndex === month
        )
        if (matchingMonthEntry === undefined) {
          throw new RangeError(`Invalid month ${month} in Chinese year ${year}`)
        }
        monthCode = buildMonthCode(
          matchingMonthEntry[0].replace('bis', ''),
          matchingMonthEntry[0].indexOf('bis') !== -1
        )
      } else {
        const months = this.getMonthList(year, cache)
        let numberPart = monthCode.replace('L', 'bis').slice(1)
        if (numberPart[0] === '0') numberPart = numberPart.slice(1)
        const monthInfo = months[numberPart]
        if (!monthInfo)
          throw new RangeError(
            `Unmatched monthCode ${monthCode} in Chinese year ${year}`
          )
        if (month !== monthInfo.monthIndex) {
          throw new RangeError(
            `monthCode ${monthCode} doesn't correspond to month ${month} in Chinese year ${year}`
          )
        }
      }
      return {
        ...calendarDate,
        year,
        eraYear,
        month,
        monthCode,
        day
      }
    }
  }

  hasEra = false
}

class ChineseHelper extends ChineseBaseHelper {
  constructor () {
    super()
    this.id = 'chinese'
  }
}

class DangiHelper extends ChineseBaseHelper {
  constructor () {
    super()
    this.id = 'dangi'
  }
}

class NonIsoCalendar {
  #helper
  constructor (helper) {
    this.#helper = helper
    this.id = helper.id
  }

  get helper () {
    return this.#helper
  }

  fields (fields) {
    return fields
  }

  dateFromFields (
    fieldsParam,
    options,
    calendar
  ) {
    const fieldNames = this.fields(['day', 'month', 'monthCode', 'year'])
    fieldNames.sort()

    const fields = fieldNames.reduce((f, key) => {
      f[key] = fieldsParam[key]
      return f
    }, {})

    const overflow = getOption(
      options,
      'overflow',
      'string',
      ['constrain', 'reject'],
      'constrain'
    )

    const cache = ObjectCache.from(fields)
    const { year, month, day } = this.helper.calendarToIsoDate(fields, overflow, cache)
    const result = new PlainDate(year, month, day, calendar)
    return result
  }

  dateAdd (date, duration, overflow, calendar) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    const added = this.helper.addCalendar(
      calendarDate,
      duration,
      overflow,
      cache
    )
    const isoAdded = this.helper.calendarToIsoDate(added, 'constrain', cache)
    const { year, month, day } = isoAdded
    return new PlainDate(year, month, day, calendar)
  }

  dateUntil (one, two, largestUnit) {
    const cacheOne = ObjectCache.from(one)
    const cacheTwo = ObjectCache.from(two)
    const calendarOne = this.helper.toCalendarDate(one, cacheOne)
    const calendarTwo = this.helper.toCalendarDate(two, cacheTwo)
    const result = this.helper.untilCalendar(
      calendarOne,
      calendarTwo,
      largestUnit,
      cacheOne
    )
    return result
  }

  year (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.year
  }

  month (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.month
  }

  day (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.day
  }

  era (date) {
    if (!this.helper.hasEra) return undefined
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.era
  }

  eraYear (date) {
    if (!this.helper.hasEra) return undefined
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.eraYear
  }

  monthCode (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    return calendarDate.monthCode
  }

  dayOfWeek (date) {
    return calendars.get('iso8601').dayOfWeek(date)
  }

  dayOfYear (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.isoToCalendarDate(date, cache)
    const startOfYear = this.helper.startOfCalendarYear(calendarDate)
    const diffDays = this.helper.calendarDaysUntil(
      startOfYear,
      calendarDate,
      cache
    )
    return diffDays + 1
  }

  weekOfYear (date) {
    return calendars.get('iso8601').weekOfYear(date)
  }

  daysInWeek (date) {
    return calendars.get('iso8601').daysInWeek(date)
  }

  daysInMonth (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    const max = this.helper.maximumMonthLength(calendarDate)
    const min = this.helper.minimumMonthLength(calendarDate)
    if (max === min) return max

    const startOfMonthCalendar = this.helper.startOfCalendarMonth(calendarDate)
    const startOfNextMonthCalendar = this.helper.addMonthsCalendar(
      startOfMonthCalendar,
      1,
      'constrain',
      cache
    )
    const result = this.helper.calendarDaysUntil(
      startOfMonthCalendar,
      startOfNextMonthCalendar,
      cache
    )
    return result
  }

  daysInYear (dateParam) {
    let date = dateParam
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    const startOfYearCalendar = this.helper.startOfCalendarYear(calendarDate)
    const startOfNextYearCalendar = this.helper.addCalendar(
      startOfYearCalendar,
      { years: 1 },
      'constrain',
      cache
    )
    const result = this.helper.calendarDaysUntil(
      startOfYearCalendar,
      startOfNextYearCalendar,
      cache
    )
    return result
  }

  monthsInYear (date) {
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    const result = this.helper.monthsInYear(calendarDate, cache)
    return result
  }

  inLeapYear (dateParam) {
    let date = dateParam
    const cache = ObjectCache.from(date)
    const calendarDate = this.helper.toCalendarDate(date, cache)
    const result = this.helper.inLeapYear(calendarDate, cache)
    return result
  }
}

for (const Helper of [
  HebrewHelper,
  PersianHelper,
  EthiopicHelper,
  EthioaaHelper,
  CopticHelper,
  ChineseHelper,
  DangiHelper,
  RocHelper,
  IndianHelper,
  BuddhistHelper,
  GregoryHelper,
  JapaneseHelper,
  IslamicHelper,
  IslamicUmalquraHelper,
  IslamicTblaHelper,
  IslamicCivilHelper,
  IslamicRgsaHelper,
  IslamicCcHelper
]) {
  const helper = new Helper()
  calendars.set(helper.id, new NonIsoCalendar(helper))
}
